import { useEffect, useState } from 'react';
import ReportActions from './ReportActions';
import StatusChip from '../StatusChip';
import { tx } from '../../i18n';
import { formatPence } from '../../lib/format';
import { verdictShort } from '../../lib/report';
import { statusOfVerdict } from '../../lib/status';
import type { AnalysisDetailResponse, AnalysisSummaryResponse } from '../../types/api';

interface ReportAsideProps {
  summary: AnalysisSummaryResponse;
  detail: AnalysisDetailResponse | null;
  propertyAddressId: string | null;
  /** Section ids that exist on this page, with the label to show for each. */
  sections: Array<{ id: string; label: string }>;
  onRefresh: (() => void) | null;
  refreshing: boolean;
}

/**
 * Which section is on screen, so the contents can say where the reader is.
 *
 * <p>A long report with everything folded away is easy to get lost in; the marker in the contents
 * is the cheapest possible answer to "where am I". The topmost section intersecting the viewport
 * wins, because with sections open and closed at the same time there is rarely only one.
 */
function useCurrentSection(ids: string[]): string | null {
  const [current, setCurrent] = useState<string | null>(null);
  const key = ids.join('|');

  useEffect(() => {
    const sections = key
      .split('|')
      .filter(Boolean)
      .map((id) => document.getElementById(id))
      .filter((node): node is HTMLElement => node !== null);
    if (sections.length === 0 || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visible.add(entry.target.id);
          } else {
            visible.delete(entry.target.id);
          }
        }
        const first = sections.find((section) => visible.has(section.id));
        setCurrent(first ? first.id : null);
      },
      { rootMargin: '-80px 0px -60% 0px' },
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [key]);

  return current;
}

/**
 * The verdict, the actions and the way around the page, held beside the report.
 *
 * <p>A listing keeps the agent's card in view while the reader scrolls; the equivalent here is the
 * score and what to do with it. On a phone there is no room for a column, so it becomes the first
 * block of the page and stops sticking.
 */
export default function ReportAside({
  summary,
  detail,
  propertyAddressId,
  sections,
  onRefresh,
  refreshing,
}: ReportAsideProps) {
  const monthly = detail?.runningCostPence ? Math.round(detail.runningCostPence / 12 / 100) * 100 : null;
  const current = useCurrentSection(sections.map((section) => section.id));

  return (
    <aside className="report-aside">
      <div className="report-aside__card">
        {/* The full verdict is at the top of the report; this is the reminder that follows the
            reader down the page, so it is a line rather than a block. */}
        <p className="report-aside__score">
          <span className="report-aside__number">
            {summary.overall.score}
            <span className="report-aside__of">/100</span>
          </span>
          <StatusChip status={statusOfVerdict(summary.overall.label)} label={verdictShort(summary.overall.label)} />
        </p>

        {monthly !== null ? (
          <p className="small muted">
            {tx(
              `Energy, roughly ${formatPence(monthly)} a month`,
              `Enerji, ayda yaklaşık ${formatPence(monthly)}`,
              `Energia, około ${formatPence(monthly)} miesięcznie`,
              `Energia, cerca de ${formatPence(monthly)} por mês`,
              `Energía, unos ${formatPence(monthly)} al mes`,
            )}
          </p>
        ) : null}

        <ReportActions
          summary={summary}
          detail={detail}
          propertyAddressId={propertyAddressId}
          onRefresh={onRefresh}
          refreshing={refreshing}
        />
      </div>

      {sections.length > 0 ? (
        <nav
          className="report-aside__nav"
          aria-label={tx('On this page', 'Bu sayfada', 'Na tej stronie', 'Nesta página', 'En esta página')}
        >
          <p className="label">
            {tx('On this page', 'Bu sayfada', 'Na tej stronie', 'Nesta página', 'En esta página')}
          </p>
          <ul>
            {sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className={current === section.id ? 'is-current' : undefined}
                  aria-current={current === section.id ? 'true' : undefined}
                >
                  {section.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}
    </aside>
  );
}
